import React, { useEffect } from 'react'

import { Box, Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { Barcode } from 'src/components/Barcode'

import { convertGS1 } from './utils'

const useStyles = makeStyles({
  label: {
    width: '4in',
    height: '6in',
  },
  zoneA: {
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2in',
    fontSize: '14px',
  },
  zoneB: {
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2in',
    fontSize: '14px',
  },
  zoneC: {
    borderTop: '1px solid black',
    borderBottom: '1px solid black',
    borderRight: '1px solid black',
    height: '1in',
    width: '2in',
  },
  zoneD: {
    borderTop: '1px solid black',
    borderBottom: '1px solid black',
    borderLeft: '1px solid black',
    height: '1in',
    width: '2in',
  },
  zoneE: {
    borderTop: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '4in',
  },
  zoneF: {
    borderTop: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '4in',
  },
  zoneG: {
    borderTop: '1px solid black',
    height: '1in',
    width: '4in',
  },
  header: {
    fontSize: '12px',
    fontWeight: 'bold',
    paddingLeft: '3px',
  },
  address: {
    lineHeight: '1.0',
    paddingLeft: '10px',
  },
  from: {
    lineHeight: '1.0',
    paddingLeft: '5px',
    fontSize: '10px',
  },
  carrier: {
    lineHeight: '1.25',
  },
  to: {
    lineHeight: '1.0',
    fontWeight: 'bold',
    paddingLeft: '10px',
  },
  container: {
    top: 'auto',
    bottom: 0,
  },
  center: {
    flexGrow: 1,
    textAlign: 'center',
  },
})

interface AmazonLabelProps {
  zoneA: string[]
  zoneB: string[]
  zoneC: {
    name: string
    pro: string
    bol: string
    routing_number: string
  }
  zoneD: {
    po: string
  }
  zoneE: {
    pallet_index: [number, number]
    cartons: number
  }
  zoneF: string
  zoneG: string
}

interface Props {
  data: AmazonLabelProps
  onReadyToPrint?: () => void
}

export const AmazonLabel = (props: Props) => {
  const { data, onReadyToPrint } = props
  const ssccBarcode = { barcode: convertGS1(data.zoneG, 2), type: 'gs1-128', displayName: '' }
  const poBarcode = { barcode: data.zoneD.po, type: 'code128', displayName: '' }
  const classes = useStyles()

  useEffect(() => {
    onReadyToPrint?.()
  }, [onReadyToPrint])

  return (
    <div className={`always-page-break ${classes.label}`}>
      <div className="print-page">
        <Box className={classes.label}>
          <Grid container spacing={0}>
            <Grid item>
              <Box className={classes.zoneA}>
                <Typography className={classes.header}>From:</Typography>
                {data.zoneA.map((line) => (
                  <Typography key={line} className={classes.from}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneB}>
                <Typography className={classes.header}>To:</Typography>
                {data.zoneB.map((line) => (
                  <Typography key={line} className={classes.to}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneC} pl={1}>
                <Box>
                  <Typography className={classes.carrier}>CARRIER: {data.zoneC.name}</Typography>
                </Box>
                <Box>
                  <Typography className={classes.carrier}>BOL #: {data.zoneC.bol}</Typography>
                </Box>
                <Box>
                  <Typography className={classes.carrier}>PRO #: {data.zoneC.pro}</Typography>
                </Box>
                <Box>
                  <Typography className={classes.carrier}>ARN: {data.zoneC.routing_number}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneD}>
                <Typography className={classes.header}>PO(s):</Typography>
                <Box px={2}>
                  <Barcode barcode={poBarcode} scale={2} height={7} />
                </Box>
                <Box display="flex" justifyContent="center" mt={0} pt={0}>
                  <Typography variant="h6">{poBarcode.barcode}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneE} display="flex">
                <Box width="50%" pl={1}>
                  <Typography variant="h6">
                    Pallet {data.zoneE.pallet_index[0]} of {data.zoneE.pallet_index[1]}
                  </Typography>
                </Box>
                <Box width="50%" pl={1}>
                  <Typography variant="h6">
                  Cartons on pallet: {data.zoneE.cartons}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneF} display="flex" alignItems="center" justifyContent="center">
                <Typography variant="h4">
                  {data.zoneF === "Mixed SKU" ? "MIXED SKU" : `SINGLE ASIN - ${data.zoneF}`}
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box className={`${classes.zoneG} ${classes.container}`}>
                <Box display="flex" alignContent="center" justifyContent="center">
                  <Typography className={classes.header}>Pallet SSCC</Typography>
                </Box>
                <Box ml={5} mr={5}>
                  <Barcode barcode={ssccBarcode} scale={2} height={20} />
                </Box>
                <Box display="flex" justifyContent="center">
                  <Typography variant="h3">{ssccBarcode.barcode}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </div>
    </div>
  )
}
